import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Award, Users, Factory, Globe, Check, ArrowRight } from 'lucide-react';
import Footer from '../../components/Footer';
import { scaleIn } from '../../hooks/useScrollAnimation';

const About = () => {
  const stats = [
    { number: '6+', label: 'Years Experience' },
    { number: '100+', label: 'Projects Completed' },
    { number: '50+', label: 'Happy Clients' },
    { number: '3', label: 'Provinces Served' },
  ];

  const values = [
    { icon: Award, title: 'Quality First', desc: 'ISO 9001 certified processes ensure every product meets international standards.' },
    { icon: Users, title: 'Customer Focus', desc: 'We listen, advise, and deliver solutions tailored to your specific needs.' },
    { icon: Factory, title: 'Local Manufacturing', desc: 'Made in South Africa, supporting local jobs and ensuring faster delivery.' },
    { icon: Globe, title: 'SADC Expansion', desc: 'Proudly serving South Africa and expanding across the SADC region.' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }
    }
  };

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="w-full bg-charcoal py-20 lg:py-32 overflow-hidden">
        <div className="px-6 lg:px-[7vw]">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-10 mb-6"
          >
            <motion.img 
              src="/logo.webp" 
              alt="Molomo NR Logo" 
              className="h-20 lg:h-28 w-auto"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            />
            <h1 className="font-display font-bold text-white text-4xl lg:text-6xl">
              About Molomo NR
            </h1>
          </motion.div>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-white/60 max-w-2xl text-lg"
          >
            Building storage solutions since 2018. A 100% black-owned South African company specializing in industrial storage.
          </motion.p>
        </div>
      </section>

      {/* Story */}
      <section className="w-full bg-offwhite py-16 lg:py-24">
        <div className="px-6 lg:px-[7vw]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-display font-bold text-charcoal text-2xl lg:text-3xl mb-4">
                Our Story
              </h2>
              <p className="text-charcoal/70 mb-4 leading-relaxed">
                Founded in 2018, Molomo NR started with a simple mission: to provide high-quality, 
                affordable storage solutions to South African businesses. What began as a small operation 
                has grown into a trusted partner for warehouses, retailers, and manufacturers across the country.
              </p>
              <p className="text-charcoal/70 mb-4 leading-relaxed">
                Our name "Molomo" reflects our commitment to open communication with our clients—we listen 
                to your needs and deliver solutions that work. With state-of-the-art manufacturing facilities 
                in Johannesburg and a team of experienced professionals, we handle everything from design to installation.
              </p>
              <p className="text-charcoal/70 leading-relaxed">
                One of our key strengths is the wealth of knowledge our team brings to every project. 
                We work closely with clients to understand their unique requirements, delivering custom solutions 
                that are both functional and cost-effective. This dedication to excellence and customer satisfaction 
                has earned us a solid reputation across South Africa and the SADC region.
              </p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src="/images/aboutUs.webp" 
                  alt="Molomo NR technician installing racking system" 
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              {/* Experience Badge */}
              <motion.div 
                className="absolute -bottom-6 -left-6 bg-lime rounded-xl p-4 shadow-lg"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, type: "spring" }}
              >
                <div className="font-display font-bold text-charcoal text-3xl">6+</div>
                <div className="text-charcoal/70 text-sm">Years Experience</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* B-BBEE Section */}
      <section className="w-full bg-white py-16 lg:py-24">
        <div className="px-6 lg:px-[7vw]">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-charcoal rounded-2xl p-8 lg:p-12 text-white"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <Award className="w-12 h-12 text-lime mb-4" />
                <h3 className="font-display font-bold text-2xl lg:text-3xl mb-3">B-BBEE Level 1 Certified</h3>
                <p className="text-white/70 mb-6">
                  As a B-BBEE Level 1 contributor, we help our clients meet their procurement requirements 
                  while supporting economic transformation in South Africa.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 text-sm text-white/80 bg-white/10 px-4 py-2 rounded-full">
                    <Check className="w-4 h-4 text-lime" />
                    100% black-owned
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/80 bg-white/10 px-4 py-2 rounded-full">
                    <Check className="w-4 h-4 text-lime" />
                    Government tender ready
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white/80 bg-white/10 px-4 py-2 rounded-full">
                    <Check className="w-4 h-4 text-lime" />
                    Empowering local communities
                  </div>
                </div>
              </div>
              <div className="flex justify-center lg:justify-end">
                <a 
                  href="/B-BBEEMolomoNR.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-lime text-charcoal px-6 py-4 rounded-xl font-medium hover:bg-lime/90 transition-colors"
                >
                  <Award className="w-5 h-5" />
                  Download B-BBEE Certificate
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="w-full bg-charcoal py-16 lg:py-24">
        <div className="px-6 lg:px-[7vw]">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div 
                key={index} 
                variants={itemVariants}
                whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                className="text-center"
              >
                <motion.div 
                  className="font-display font-bold text-lime text-4xl lg:text-5xl mb-2"
                  initial={{ scale: 0.5, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ 
                    type: "spring",
                    stiffness: 200,
                    damping: 15,
                    delay: index * 0.1 
                  }}
                >
                  {stat.number}
                </motion.div>
                <div className="text-white/60 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="w-full bg-offwhite py-16 lg:py-24">
        <div className="px-6 lg:px-[7vw]">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-display font-bold text-charcoal text-2xl lg:text-3xl mb-3">
              Our Values
            </h2>
            <p className="text-charcoal/60 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="bg-white rounded-xl p-6 shadow-sm"
              >
                <motion.div 
                  className="w-12 h-12 rounded-lg bg-lime/10 flex items-center justify-center mb-4"
                  whileHover={{ rotate: 10, scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                >
                  <value.icon className="w-6 h-6 text-charcoal" />
                </motion.div>
                <h3 className="font-display font-bold text-charcoal mb-2">{value.title}</h3>
                <p className="text-charcoal/60 text-sm">{value.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full bg-charcoal py-16 lg:py-24">
        <div className="px-6 lg:px-[7vw] text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display font-bold text-white text-2xl lg:text-3xl mb-4">
              Let's work together
            </h2>
            <p className="text-white/60 max-w-xl mx-auto mb-6">
              Ready to transform your storage space? Get in touch for a free consultation and quote.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block"
            >
              <Link
                to="/contact"
                className="bg-lime text-charcoal px-8 py-4 rounded-lg font-medium inline-flex items-center gap-2 hover:bg-lime/90 transition-colors"
              >
                Get in Touch
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
